import React, { useContext } from 'react'
import styled from 'styled-components'
import ButtonArrow from '@components/Button/Button.Arrow'
import Heading from '@components/Heading'
import Section from '@components/Section'
import ScrollIndicator from '@components/ScrollIndicator'
import ShapeShifter from '@components/ShapeShifter'
import IntersectionObserver from '@components/IntersectionObserver'
import Transitions from '@components/Transitions'
import LayoutHeroMobile from '@components/Layout/Layout.Hero.Mobile'
import { ContactContext } from '@components/Contact/Contact.Context'

import media from '@styles/media'
import { SITE_NAME } from '@utils/siteConfig'


const heroImageUrl = 'https://static.vecteezy.com/system/resources/thumbnails/013/087/516/small/diagonal-golden-line-glass-cube-on-black-background-illustration-of-website-banner-poster-sign-corporate-business-social-media-post-billboard-agency-advertising-media-motion-video-animation-wave-vector.jpg'


function HomeHero() {
  const { toggleContact } = useContext(ContactContext)

  return (
    <LayoutHeroMobile>
      <HomeHeroContainer id="home-hero">
        <Section>
          <IntersectionObserver
            render={({ intersectionRatio: ir }) => (
              <ContentContainer
                style={ir ? { opacity: ir * ir } : { opacity: 1 }}
              >
                <TextContainer>
                  <Transitions.CSS.FadeIn>
                    <Heading.h1>
                      Building Intelligent Systems for Everyday Life
                    </Heading.h1>
                    <MainText>
                      We design and deploy technology that solves real-world inefficiencies across healthcare, commerce, and wellbeing. Our platforms are built to integrate seamlessly into existing systems—enhancing, not replacing.
                    </MainText>
                    <FlagshipLabel>Our Flagship Products</FlagshipLabel>
                    <ProductLogos>
                      <ProductLogo>
                        <img src="/images/apps/swasthaid-logo.png" alt="SwasthaID" />
                        <span>SwasthaID</span>
                      </ProductLogo>
                      <ProductLogo>
                        <img src="/images/apps/revvlow-logo.svg" alt="Revvlow" onError={(e) => { (e.target as HTMLImageElement).src = '/images/apps/revvlow.jpg' }} />
                        <span>Revvlow</span>
                      </ProductLogo>
                      <ProductLogo>
                        <OsadLogoMini>
                          <OsadLeafMini />
                          <span>OsadAI</span>
                        </OsadLogoMini>
                        <span>OsadAI</span>
                      </ProductLogo>
                    </ProductLogos>
                    <Contact onClick={toggleContact}>
                      To contact us, Press <Key>C</Key>
                    </Contact>
                    <Mobile>
                      <ButtonArrow
                        as="button"
                        onClick={toggleContact}
                        text="Get in touch"
                      />
                    </Mobile>
                  </Transitions.CSS.FadeIn>
                </TextContainer>

                <ImageContainer desktop>
                  <HeroImageBottom>
                    <img src={heroImageUrl} style={{ width: '100%', display: 'block' }} />
                  </HeroImageBottom>
                </ImageContainer>


              </ContentContainer>
            )}
          />
          {/* <ScrollIndicator /> */}
        </Section>
      </HomeHeroContainer>
    </LayoutHeroMobile>
  )
}

export default HomeHero

const HomeHeroContainer = styled.div`
  ${media.desktop`
    #mirror-mask {
      display: none;
  `}
`



const HeroImageBottom = styled.div<{ start: boolean }>`
  max-width: 381.46px;
  margin: 0 auto;
  transform: translateY(${p => (p.start ? '0' : '60px')});
  transition: transform 1.4s var(--ease-in-out-cubic);

  ${p =>
    p.start &&
    css`
      animation: ${float} 4.8s ease-in-out infinite 1.4s;
    `}

  ${media.tablet`
    transform: none;
  `}
`

const ImageContainer = styled.div`
  position: absolute;
  width: 100%;
  width: 460px;
  right: -20px;
  bottom: 40px;
  justify-self: flex-end;

  ${media.desktop_medium`
    right: 2%;
  `};

  ${media.desktop`
    display:  ${p => (p.desktop ? ' none' : 'block')};
    background: #08070b;
    right: 0;
    left: 0;
    position: relative;
    margin: 0 auto;
    padding-bottom: 60px;
    width: 100%;
  `};
`

const TextContainer = styled.div`
  position: relative;
  z-index: 10;
  max-width: 570px;
  top: 10px;

  ${media.phablet`
    position: relative;
  `}
`

const MainText = styled.p`
  font-size: 2.2rem;
  font-weight: 300;
  color: ${p => p.theme.colors.grey};
  line-height: 1.3;
  margin-bottom: 50px;
  font-family: monospace;

  ${media.phablet`
    font-size: 2.2rem;
  `};
`

const ContentContainer = styled.div`
  height: calc(100vh - 230px);
  min-height: 600px;
  padding-top: 10px;

  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;

  ${media.phablet`
    height: calc(100vh - 180px);
    min-height: 100%;
    padding: 0;
    top: -40px;
  `};

  ${media.desktop_medium`
    min-height: 360px;
  `};

  @media screen and (max-height: 800px) {
    min-height: 360px;
  }

  @media screen and (max-height: 648px) {
    top: -60px;
  }
`

const Contact = styled.button`
  color: ${p => p.theme.colors.grey};
  font-weight: 600;
  font-style: italic;
  transition: color 0.25s ease;

  &:hover {
    color: #fff;

    span {
      background: #fff;
    }
  }

  ${media.desktop`
    display: none;
  `};
`

const Key = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin: 0 1px;
  color: gold;
  background: ${p => p.theme.colors.white};
  font-size: 13px;
  width: 16px;
  height: 16px;
  border-radius: 2.5px;
  transition: background 0.25s ease;
`

const Mobile = styled.div`
  ${media.desktop_medium_up`
    display: none;
  `};
`

const FlagshipLabel = styled.p`
  font-size: 1.2rem;
  font-weight: 600;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: ${p => p.theme.colors.grey};
  margin-bottom: 16px;
  opacity: 0.6;
`

const ProductLogos = styled.div`
  display: flex;
  align-items: center;
  gap: 32px;
  margin-bottom: 40px;

  ${media.phablet`
    gap: 20px;
  `}
`

const ProductLogo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;

  img {
    height: 36px;
    width: auto;
    object-fit: contain;
    filter: brightness(0.9);
  }

  span {
    font-size: 1.1rem;
    color: ${p => p.theme.colors.grey};
    opacity: 0.7;
  }
`

const OsadText = styled.span`
  font-size: 2rem;
  font-weight: 700;
  color: #2d7a3a;
  letter-spacing: -0.5px;
  line-height: 36px;
`

const OsadLogoMini = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`

const OsadLeafMini = styled.div`
  width: 24px;
  height: 24px;
  background: #2d7a3a;
  border-radius: 50% 0 50% 0;
  transform: rotate(45deg);
  flex-shrink: 0;
`
